import { useState, useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable, { sampleSeriesToDaily } from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

type Granularity = "7d" | "30d" | "90d" | "all";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function subDaysMs(ms: number, days: number): number {
  return ms - days * 24 * 60 * 60 * 1000;
}

export default function EquityCurveWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();
  const [granularity, setGranularity] = useState<Granularity>("all");

  const filteredPoints = useMemo(() => {
    if (!analyticsData || analyticsData.cumulativePnl.length === 0) return [];
    const points = analyticsData.cumulativePnl;
    if (granularity === "all") return points;
    const now = Date.now();
    const days = granularity === "7d" ? 7 : granularity === "30d" ? 30 : 90;
    const cutoff = subDaysMs(now, days);
    const filtered = points.filter((p) => p.x >= cutoff);
    if (filtered.length === 0) return points.slice(-1);
    return filtered;
  }, [analyticsData, granularity]);

  const tableColumns = [
    { key: "date", label: t.analytics.dateLabel, align: "left" as const },
    { key: "value", label: t.analytics.valueLabel, align: "right" as const },
  ];
  const tableRows = sampleSeriesToDaily(analyticsData?.cumulativePnl ?? []).map((p) => ({
    date: new Date(p.x).toLocaleDateString(undefined),
    value: (
      <span className={p.y >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {p.y >= 0 ? "+" : ""}
        {p.y.toFixed(2)}
      </span>
    ),
  }));

  if (!analyticsData || analyticsData.cumulativePnl.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.analytics.cumulativePnl}
          isDark={isDark}
          rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
        />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const totalPnl =
    filteredPoints.length > 0
      ? filteredPoints[filteredPoints.length - 1].y - filteredPoints[0].y
      : 0;
  const curveColor = pnlColor(totalPnl);
  const c = chartColors();

  const chartOptions = {
    chart: {
      type: "area" as const,
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: false },
      foreColor: c.axis,
    },
    stroke: { curve: "smooth" as const, width: 2.5, colors: [curveColor] },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: isDark ? 0.3 : 0.2,
        opacityTo: 0.01,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color: curveColor, opacity: isDark ? 0.3 : 0.2 },
          { offset: 100, color: curveColor, opacity: 0 },
        ],
      },
    },
    xaxis: {
      type: "datetime" as const,
      labels: {
        style: { colors: c.axis, fontSize: "11px" },
        datetimeUTC: false,
        format: "dd MMM",
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      tickAmount: 5,
      labels: {
        style: { colors: c.axis, fontSize: "11px" },
        formatter: (val: number) => {
          if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)}k`;
          return val.toFixed(0);
        },
      },
      forceNiceScale: true,
    },
    grid: {
      borderColor: c.grid,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      x: { format: "dd MMM yyyy" },
      y: {
        formatter: (val: number) => `${val.toFixed(2)} $`,
        title: { formatter: () => t.analytics.cumulativePnl },
      },
    },
    markers: { size: 0 },
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
    { value: "7d", label: "7d" },
    { value: "30d", label: "30d" },
    { value: "90d", label: "90d" },
    { value: "all", label: t.analytics.allTime },
  ];

  const granularityButtons = (
    <div className="flex rounded border border-border overflow-hidden text-xs">
      {GRANULARITY_OPTIONS.map(({ value, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => setGranularity(value)}
          className={`px-2.5 py-1 transition-colors font-medium ${
            granularity === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.cumulativePnl}
        isDark={isDark}
        rightContent={
          <div className="flex items-center gap-2">
            {granularityButtons}
            <ChartViewToggle value={viewMode} onChange={onSetViewMode} />
          </div>
        }
      />
      <CardContent className="flex-1 px-5 pb-5 pt-1">
        {viewMode === "table" ? (
          <WidgetDataTable
            columns={tableColumns}
            rows={tableRows}
            emptyLabel={t.analytics.noDataAvailable}
          />
        ) : filteredPoints.length > 0 ? (
          <LazyApexChart
            key={`equity-curve-widget-${granularity}-${themeId}`}
            options={chartOptions}
            series={[{ name: t.analytics.cumulativePnl, data: filteredPoints }]}
            type="area"
            height={240}
          />
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
      </CardContent>
    </Card>
  );
}
